import { useEffect, useState } from "react";
import {
    Dialog,
    DialogType,
    DialogFooter,
    PrimaryButton,
    DefaultButton,
    TextField,
    Toggle,
    ChoiceGroup,
    IChoiceGroupOption,
    MessageBar,
    MessageBarType,
    Spinner,
} from "@fluentui/react";
import { useTranslation } from "react-i18next";

import { createShareApi, getShareSharepointMetadataApi, CreateSharePayload, CreateShareResponse } from "../../api";

interface Props {
    isOpen: boolean;
    sharepointUrl: string;
    fileName: string;
    onDismiss: () => void;
    onCreated?: (response: CreateShareResponse) => void;
}

const EMPTY_RECIPIENT = { firstName: "", lastName: "", email: "", company: "", phone: "" };

export const ShareDocumentDialog = ({ isOpen, sharepointUrl, fileName, onDismiss, onCreated }: Props) => {
    const { t } = useTranslation();

    const [recipient, setRecipient] = useState({ ...EMPTY_RECIPIENT });
    const [nda, setNda] = useState(false);
    const [dpa, setDpa] = useState(false);
    // SharePoint's ApprovalDownload column. We only display this to the
    // sender as a warning — the backend re-reads the same column at
    // share-create time and enforces it. Null = lookup failed/unknown,
    // treated the same as "not allowed" for the warning.
    const [sharepointAllowsDownload, setSharepointAllowsDownload] = useState<boolean | null>(null);
    const [expiryDays, setExpiryDays] = useState<1 | 3 | 7>(7);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<CreateShareResponse | null>(null);
    const [metaLoading, setMetaLoading] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setRecipient({ ...EMPTY_RECIPIENT });
        setNda(false);
        setDpa(false);
        setSharepointAllowsDownload(null);
        setExpiryDays(7);
        setError(null);
        setResult(null);

        let cancelled = false;
        setMetaLoading(true);
        getShareSharepointMetadataApi(sharepointUrl)
            .then(meta => {
                if (cancelled) return;
                setSharepointAllowsDownload(meta.downloadAllowed);
            })
            .catch(() => {
                /* non-fatal: leave as null → warning shown */
            })
            .finally(() => {
                if (!cancelled) setMetaLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [isOpen, sharepointUrl]);

    const expiryOptions: IChoiceGroupOption[] = [
        { key: "1", text: t("share.expiry1") },
        { key: "3", text: t("share.expiry3") },
        { key: "7", text: t("share.expiry7") },
    ];

    const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient.email.trim());
    const allFilled =
        recipient.firstName.trim() &&
        recipient.lastName.trim() &&
        validEmail &&
        recipient.company.trim() &&
        recipient.phone.trim();

    const submit = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const payload: CreateSharePayload = {
                sharepointUrl,
                recipient: {
                    firstName: recipient.firstName.trim(),
                    lastName: recipient.lastName.trim(),
                    email: recipient.email.trim(),
                    company: recipient.company.trim(),
                    phone: recipient.phone.trim(),
                },
                flags: { nda, dpa },
                expiryDays,
            };
            const res = await createShareApi(payload);
            setResult(res);
            onCreated?.(res);
        } catch (e: any) {
            setError(e?.message || "Unknown error");
        } finally {
            setSubmitting(false);
        }
    };

    const copyShareUrl = async () => {
        if (!result) return;
        try {
            await navigator.clipboard.writeText(result.shareUrl);
        } catch {
            /* ignore */
        }
    };

    return (
        <Dialog
            hidden={!isOpen}
            onDismiss={onDismiss}
            dialogContentProps={{
                type: DialogType.normal,
                title: t("share.title"),
                subText: fileName,
            }}
            modalProps={{ isBlocking: submitting }}
            minWidth={620}
        >
            {result ? (
                <div className="space-y-3">
                    <MessageBar messageBarType={MessageBarType.success}>{t("share.success")}</MessageBar>
                    <TextField
                        label={t("share.shareUrl")}
                        value={result.shareUrl}
                        readOnly
                        onClick={(e: any) => e.target.select()}
                    />
                    <DialogFooter>
                        <DefaultButton onClick={copyShareUrl} text={t("share.copyLink")} />
                        <PrimaryButton onClick={onDismiss} text={t("share.close")} />
                    </DialogFooter>
                </div>
            ) : (
                <div className="space-y-3">
                    {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
                    {metaLoading && (
                        <div className="text-xs text-gray-500 flex items-center gap-2">
                            <Spinner /> {t("share.loadingMetadata")}
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                        <TextField
                            label={t("share.firstName")}
                            value={recipient.firstName}
                            onChange={(_, v) => setRecipient(r => ({ ...r, firstName: v || "" }))}
                            required
                        />
                        <TextField
                            label={t("share.lastName")}
                            value={recipient.lastName}
                            onChange={(_, v) => setRecipient(r => ({ ...r, lastName: v || "" }))}
                            required
                        />
                        <TextField
                            label={t("share.email")}
                            type="email"
                            value={recipient.email}
                            onChange={(_, v) => setRecipient(r => ({ ...r, email: v || "" }))}
                            required
                            errorMessage={recipient.email && !validEmail ? t("share.emailInvalid") : undefined}
                        />
                        <TextField
                            label={t("share.company")}
                            value={recipient.company}
                            onChange={(_, v) => setRecipient(r => ({ ...r, company: v || "" }))}
                            required
                        />
                        <TextField
                            label={t("share.phone")}
                            value={recipient.phone}
                            onChange={(_, v) => setRecipient(r => ({ ...r, phone: v || "" }))}
                            required
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-x-4 pt-2">
                        <Toggle
                            label={t("share.ndaLabel")}
                            checked={nda}
                            onChange={(_, c) => setNda(!!c)}
                            onText={t("share.yes")}
                            offText={t("share.no")}
                        />
                        <Toggle
                            label={t("share.dpaLabel")}
                            checked={dpa}
                            onChange={(_, c) => setDpa(!!c)}
                            onText={t("share.yes")}
                            offText={t("share.no")}
                        />
                    </div>

                    {/* SharePoint download status — informational only. The backend
                        re-reads the ApprovalDownload column at share-create time and
                        enforces it; the sender doesn't choose. We just warn them when
                        SharePoint says the file isn't downloadable so they know what
                        the recipient will see. */}
                    {!metaLoading && sharepointAllowsDownload !== true && (
                        <div className="text-red-600 font-bold text-sm pt-2">
                            {t("share.downloadLockedNotice")}
                        </div>
                    )}

                    <ChoiceGroup
                        label={t("share.expiry")}
                        options={expiryOptions}
                        selectedKey={String(expiryDays)}
                        onChange={(_, opt) => opt && setExpiryDays(Number(opt.key) as 1 | 3 | 7)}
                    />

                    <DialogFooter>
                        <DefaultButton onClick={onDismiss} text={t("share.cancel")} disabled={submitting} />
                        <PrimaryButton
                            onClick={submit}
                            text={submitting ? t("share.sending") : t("share.send")}
                            disabled={!allFilled || submitting}
                        />
                    </DialogFooter>
                </div>
            )}
        </Dialog>
    );
};
